import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { toast } from "sonner";
import { Package, Search, Calendar, Clock, CheckCircle2, XCircle, ShieldCheck } from "lucide-react";

const SYMBOLS = { INR: "\u20b9", USD: "$", EUR: "\u20ac", GBP: "\u00a3" };

const TIMELINE_STEPS = [
  { key: "pending", label: "Placed" },
  { key: "confirmed", label: "Confirmed" },
  { key: "processing", label: "Processing" },
  { key: "shipped", label: "Shipped" },
  { key: "delivered", label: "Delivered" },
];

function OrderTimeline({ status, cancellationReason }) {
  const s = (status || "pending").toLowerCase();
  if (s === "cancelled") {
    return (
      <div className="mt-3 pt-3 border-t border-border" data-testid="timeline-cancelled">
        <div className="flex items-center gap-2 text-[#dc2626]">
          <XCircle className="w-4 h-4 shrink-0" />
          <div>
            <div className="text-xs font-bold uppercase tracking-wider">Order Cancelled</div>
            {cancellationReason && (
              <div className="text-[11px] text-[#71717a] mt-0.5">Reason: {cancellationReason}</div>
            )}
          </div>
        </div>
      </div>
    );
  }
  const currentIdx = Math.max(0, TIMELINE_STEPS.findIndex((x) => x.key === s));
  return (
    <div className="mt-3 pt-3 border-t border-border" data-testid="order-timeline">
      <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#71717a] mb-2">Progress</div>
      <div className="flex items-center gap-1">
        {TIMELINE_STEPS.map((step, i) => {
          const done = i < currentIdx;
          const active = i === currentIdx;
          const dotBg = done ? "bg-[#16a34a] border-[#16a34a] text-white" : active ? "bg-[#ea580c] border-[#ea580c] text-white" : "bg-white border-[#d4d4d8] text-[#a1a1aa]";
          const labelColor = done ? "text-[#16a34a]" : active ? "text-[#ea580c]" : "text-[#a1a1aa]";
          const barColor = i < currentIdx ? "bg-[#16a34a]" : "bg-[#e4e4e7]";
          return (
            <div key={step.key} className="flex-1 flex flex-col items-center" data-testid={`timeline-step-${step.key}`}>
              <div className="flex items-center w-full">
                {i > 0 && <div className={`h-[3px] flex-1 ${barColor}`} />}
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center text-[10px] font-black ${dotBg}`}>
                  {done ? <CheckCircle2 className="w-3.5 h-3.5" /> : (i + 1)}
                </div>
                {i < TIMELINE_STEPS.length - 1 && <div className={`h-[3px] flex-1 ${i < currentIdx ? "bg-[#16a34a]" : "bg-[#e4e4e7]"}`} />}
              </div>
              <div className={`text-[9px] font-bold uppercase tracking-wider mt-1 ${labelColor}`}>{step.label}</div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

function OrderCard({ o }) {
  const created = o.created_at ? new Date(o.created_at) : null;

  // Guest lookup — privacy-first: show ONLY the progress bar, order # and date.
  // No prices, items, totals, payment method, or shipping. Ever.
  if (o.masked) {
    return (
      <div className="border-2 border-[#09090b] p-6 bg-white" data-testid={`order-${o.order_number}`}>
        <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3 mb-4">
          <div>
            <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Order</div>
            <div className="font-display font-black text-xl font-mono">{o.order_number}</div>
          </div>
          <div className="text-right">
            <div className="text-xs text-[#52525b] flex items-center gap-1 justify-end">
              <Calendar className="w-3 h-3" />
              {created ? created.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}
            </div>
            <div className="text-[10px] text-[#71717a] flex items-center gap-1 justify-end mt-0.5">
              <Clock className="w-3 h-3" />
              {created ? created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : ""}
            </div>
          </div>
        </div>
        <OrderTimeline status={o.status} cancellationReason={o.cancellation_reason} />
        <div className="mt-4 pt-3 border-t border-dashed border-[#e4e4e7] text-[10px] text-[#71717a] flex items-start gap-1.5">
          <ShieldCheck className="w-3 h-3 shrink-0 mt-0.5 text-[#16a34a]" />
          <span>
            <b className="text-[#09090b] uppercase tracking-wider">Privacy first:</b>{" "}
            For your security, order items, prices and personal details are never shown on this page. Sign in to your account or contact us for the full receipt.
          </span>
        </div>
      </div>
    );
  }

  const sym = SYMBOLS[(o.currency || "INR").toUpperCase()] || "\u20b9";
  const paymentPretty = (o.payment_method || "bank_transfer").replace("_", " ").toUpperCase();
  const paidBadge = o.payment_status === "paid"
    ? "bg-[#dcfce7] text-[#166534]"
    : "bg-[#fef3c7] text-[#92400e]";
  return (
    <div className="border border-border p-5 hover:border-[#09090b] transition-colors" data-testid={`order-${o.order_number}`}>
      <div className="flex flex-wrap items-start justify-between gap-3 border-b border-border pb-3 mb-3">
        <div>
          <div className="text-xs font-bold tracking-[0.2em] uppercase text-[#ea580c]">Order</div>
          <div className="font-display font-black text-lg font-mono">{o.order_number}</div>
        </div>
        <div className="text-right">
          <div className="text-xs text-[#52525b] flex items-center gap-1 justify-end">
            <Calendar className="w-3 h-3" />
            {created ? created.toLocaleDateString(undefined, { day: "2-digit", month: "short", year: "numeric" }) : "-"}
          </div>
          <div className="text-[10px] text-[#71717a] flex items-center gap-1 justify-end mt-0.5">
            <Clock className="w-3 h-3" />
            {created ? created.toLocaleTimeString(undefined, { hour: "2-digit", minute: "2-digit" }) : ""}
          </div>
        </div>
        <div className="text-right">
          <div className="font-display font-black text-xl">{sym}{Number(o.total || 0).toFixed(2)}</div>
          <div className="text-[10px] text-[#71717a] uppercase font-bold tracking-wider mt-1">{o.currency || "INR"}</div>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-[#09090b] bg-[#fff7ed]`}>
          {o.status || "pending"}
        </span>
        <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 ${paidBadge}`}>
          {o.payment_status || "unpaid"}
        </span>
        <span className="text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 border border-[#e4e4e7] text-[#52525b]">
          {paymentPretty}
        </span>
      </div>

      <div className="space-y-1 text-sm">
        {(o.items || []).map((it, idx) => (
          <div key={`${o.order_number}-${idx}`} className="flex justify-between gap-2">
            <div className="truncate">
              <span className="font-semibold">{it.quantity}×</span> {it.title}
              {it.isbn && <span className="text-[10px] text-[#71717a] ml-2 font-mono">ISBN: {it.isbn}</span>}
            </div>
            <div className="shrink-0 font-semibold">{sym}{Number(it.subtotal || 0).toFixed(2)}</div>
          </div>
        ))}
      </div>

      {o.shipping && (o.shipping.address || o.shipping.city) && (
        <div className="mt-3 pt-3 border-t border-border text-[11px] text-[#71717a]">
          <span className="font-bold text-[#09090b]">Shipping:</span> {[o.shipping.address, o.shipping.city, o.shipping.state, o.shipping.pincode].filter(Boolean).join(", ")}
        </div>
      )}

      {/* Order progress timeline — visible on ALL cards */}
      <OrderTimeline status={o.status} cancellationReason={o.cancellation_reason} />

      {o.masked && (
        <div className="mt-3 pt-3 border-t border-border text-[10px] text-[#71717a] flex items-center gap-1">
          <span className="font-bold text-[#09090b] uppercase tracking-wider">Privacy:</span>
          Personal details are hidden on this page for your security.
        </div>
      )}
    </div>
  );
}

export default function Orders() {
  const location = useLocation();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [authed, setAuthed] = useState(false);
  const [lookupEmail, setLookupEmail] = useState("");
  const [lookupOrder, setLookupOrder] = useState("");
  const [lookupMobile, setLookupMobile] = useState("");
  const [lookupBusy, setLookupBusy] = useState(false);

  // Try authenticated /orders first; if it 401s, fall back to guest lookup UI
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/orders");
        setOrders(data);
        setAuthed(true);
      } catch (e) {
        setAuthed(false);
      }
      setLoading(false);
    })();
  }, [location.pathname]);

  const runLookup = async (e) => {
    e.preventDefault();
    if (!lookupEmail) { toast.error("Please enter your email"); return; }
    if (!lookupOrder.trim()) { toast.error("Please enter your order number"); return; }
    const mobileDigits = (lookupMobile || "").replace(/\D+/g, "");
    if (mobileDigits.length < 6) { toast.error("Please enter the mobile number used at checkout"); return; }
    setLookupBusy(true);
    try {
      const { data } = await api.post("/orders/lookup", {
        email: lookupEmail.trim(),
        order_number: lookupOrder.trim(),
        mobile: lookupMobile.trim(),
      });
      // Flag results as masked so OrderCard shows the privacy-first (progress-only) view.
      setOrders((data || []).map((o) => ({ ...o, masked: true })));
      if (!data || data.length === 0) toast.info("No order matched those details.");
    } catch (err) {
      toast.error(apiError(err));
    } finally {
      setLookupBusy(false);
    }
  };

  if (loading) return (
    <div className="max-w-3xl mx-auto p-16 text-center" data-testid="orders-loading">
      <div className="inline-block w-8 h-8 border-4 border-[#e4e4e7] border-t-[#ea580c] rounded-full animate-spin" />
      <div className="text-[#52525b] mt-4">Loading your orders…</div>
    </div>
  );

  const lastOrder = orders && orders.length > 0 ? orders[0] : null;
  const totalOrders = orders.length;
  const totalSpentByCur = orders.reduce((acc, o) => {
    const c = (o.currency || "INR").toUpperCase();
    acc[c] = (acc[c] || 0) + Number(o.total || 0);
    return acc;
  }, {});

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="orders-page">
      <div className="border-b-2 border-[#09090b] pb-4 mb-6 flex items-start justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl md:text-5xl font-black">My Orders</h1>
          <div className="text-xs text-[#52525b] mt-1">
            {authed ? "Signed-in order history" : "Guest tracking — verify with email, order # and mobile"}
          </div>
        </div>
        {lastOrder && (
          <div className="border-2 border-[#ea580c] bg-[#fff7ed] px-4 py-2" data-testid="orders-summary">
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ea580c]">Last Order</div>
            <div className="font-mono font-bold text-sm">{lastOrder.order_number}</div>
            <div className="text-[10px] text-[#52525b]">
              {lastOrder.created_at ? new Date(lastOrder.created_at).toLocaleString(undefined, { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
            </div>
          </div>
        )}
      </div>

      {/* Stats strip (only when we have orders) */}
      {totalOrders > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6" data-testid="orders-stats">
          <Stat label="Total Orders" value={totalOrders} />
          {Object.entries(totalSpentByCur).map(([cur, amt]) => (
            <Stat key={cur} label={`Spent (${cur})`} value={`${SYMBOLS[cur] || ""}${Math.round(amt).toLocaleString()}`} />
          ))}
        </div>
      )}

      {/* Guest lookup form — always visible if not signed in */}
      {!authed && (
        <form onSubmit={runLookup} className="border-2 border-[#09090b] bg-[#fff7ed] p-5 mb-6 grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="guest-lookup-form">
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ea580c] mb-1">Email used at checkout *</div>
            <input
              type="email"
              value={lookupEmail}
              onChange={(e) => setLookupEmail(e.target.value)}
              required
              placeholder="you@example.com"
              className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 outline-none text-sm"
              data-testid="lookup-email"
            />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ea580c] mb-1">Order number *</div>
            <input
              value={lookupOrder}
              onChange={(e) => setLookupOrder(e.target.value)}
              required
              placeholder="GTP/0042"
              className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 outline-none text-sm font-mono"
              data-testid="lookup-order-number"
            />
          </div>
          <div>
            <div className="text-[10px] font-bold uppercase tracking-[0.2em] text-[#ea580c] mb-1">Mobile number *</div>
            <input
              type="tel"
              value={lookupMobile}
              onChange={(e) => setLookupMobile(e.target.value)}
              required
              inputMode="tel"
              placeholder="10-digit mobile"
              className="w-full border-2 border-[#e4e4e7] focus:border-[#ea580c] px-3 py-2 outline-none text-sm font-mono"
              data-testid="lookup-mobile"
            />
          </div>
          <div className="md:col-span-3 flex items-center justify-between gap-3 flex-wrap">
            <div className="text-[10px] text-[#71717a] leading-relaxed max-w-md">
              For your security we match all three fields before showing any tracking info.
              Order items, prices and personal details are never shown here.
            </div>
            <button
              type="submit"
              disabled={lookupBusy}
              className="bg-[#ea580c] hover:bg-[#c2410c] text-white font-bold px-5 py-2 flex items-center gap-2 disabled:opacity-60"
              data-testid="lookup-submit"
            >
              <Search className="w-4 h-4" /> {lookupBusy ? "Searching…" : "Track my order"}
            </button>
          </div>
        </form>
      )}

      {orders.length === 0 ? (
        <div className="border-2 border-dashed border-border p-12 text-center">
          <Package className="w-12 h-12 mx-auto text-[#a1a1aa] mb-3" />
          <div className="font-display font-bold text-lg">No orders yet</div>
          <div className="text-sm text-[#52525b]">{authed ? "Your future orders will show up here." : "Enter your email, order number and mobile above to check your order status."}</div>
        </div>
      ) : (
        <div className="space-y-4">
          {orders.map((o) => <OrderCard key={o.id || o.order_number} o={o} />)}
        </div>
      )}
    </div>
  );
}

function Stat({ label, value }) {
  return (
    <div className="border border-border bg-white p-3">
      <div className="text-[10px] font-bold uppercase tracking-[0.15em] text-[#71717a]">{label}</div>
      <div className="font-display font-black text-xl mt-1">{value}</div>
    </div>
  );
}
